import { LumberListPrototype } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { getLoggedInUserOrganization } from '@paragon/login-tools';

import { receiveMaterialListEditResponse } from './receiveMaterialListEditResponse';
import { AsyncThunkActionReturn, AsyncThunkActionVoid } from './thunkActionTypes';

export const createLumberListPrototype =
  (name: string): AsyncThunkActionReturn<string> =>
  async (dispatch) => {
    const guid = crypto.randomUUID();
    await dispatch(
      upsertLumberListPrototype({
        guid,
        name,
        isArchived: false,
        lumberSets: [],
      }),
    );

    return guid;
  };

export const duplicateLumberListPrototype =
  (lumberListGuid: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const { materialSetPrototype } = getState();
    if (materialSetPrototype.status !== 'loaded')
      throw new Error('Material set prototype not loading during duplication.');

    const sourceList = materialSetPrototype.lumberListPrototypes[lumberListGuid];
    if (sourceList == null) {
      throw new Error('Attempted to duplicate non-existent lumber list.');
    }

    const duplicatedListGuid = crypto.randomUUID();
    const duplicatedList = {
      ...sourceList,
      guid: duplicatedListGuid,
      name: `Copy of ${sourceList.name}`,
    };

    await dispatch(upsertLumberListPrototype(duplicatedList));
  };

export const upsertLumberListPrototype =
  (updatedPrototype: LumberListPrototype): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    const organization = getLoggedInUserOrganization(state);

    if (organization == null) throw new Error('Unauthenticated user attempted to edit lumber list.');

    const result = await designService.materialLists.upsertLumberList(
      organization,
      updatedPrototype.guid,
      updatedPrototype,
    );
    dispatch(receiveMaterialListEditResponse(result));
  };

export const renameLumberList =
  (lumberListGuid: string, newName: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const organization = getLoggedInUserOrganization(getState());
    if (organization == null) throw new Error('Unauthenticated user attempted to rename lumber list.');

    const response = await designService.materialLists.renameLumberList(organization, lumberListGuid, newName);
    dispatch(receiveMaterialListEditResponse(response));
  };

export const archiveLumberList =
  (lumberListGuid: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const organization = getLoggedInUserOrganization(getState());
    if (organization == null) throw new Error('Unauthenticated user attempted to archive lumber list.');

    const response = await designService.materialLists.archiveLumberList(organization, lumberListGuid);
    dispatch(receiveMaterialListEditResponse(response));
  };

export const restoreLumberList =
  (lumberListGuid: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const organization = getLoggedInUserOrganization(getState());
    if (organization == null) throw new Error('Unauthenticated user attempted to restore archived lumber list.');

    const response = await designService.materialLists.restoreLumberList(organization, lumberListGuid);
    dispatch(receiveMaterialListEditResponse(response));
  };
